import React, { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartPanel from "./ChartPanel";
import KpiCard from "./KpiCard";
import { colors } from "../utils/datosTheme";
import { formatMoney, formatMonthLabel, formatNumber } from "../utils/datosFormatters";

const pieColors = [colors.blue, colors.green, colors.amber, colors.coral, colors.teal, "#7d8495"];

function percent(value) {
  return `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`;
}

const tableCellSx = {
  color: colors.ink,
  fontSize: 12,
  borderColor: colors.line,
  whiteSpace: "nowrap",
};

export default function VehiculosDashboard({ data }) {
  const kpis = data?.kpis || {};
  const currency = kpis.moneda_principal;
  const modelos = data?.modelos || [];
  const tiposVehiculo = data?.tipos_vehiculo || [];
  const mensual = useMemo(
    () => (data?.mensual || []).map((item) => ({ ...item, label: formatMonthLabel(item.mes) })),
    [data]
  );

  const topModelos = modelos
    .filter((item) => Number(item.consultas || 0) > 0 || Number(item.ventas || 0) > 0)
    .slice(0, 10);
  const interesModelos = [...modelos]
    .filter((item) => Number(item.interes_pendiente || 0) > 0)
    .sort((a, b) => Number(b.interes_pendiente || 0) - Number(a.interes_pendiente || 0))
    .slice(0, 10);
  const conversionModelos = [...modelos]
    .filter((item) => Number(item.consultas || 0) > 0 && Number(item.conversion_pct || 0) > 0)
    .sort((a, b) => Number(b.conversion_pct || 0) - Number(a.conversion_pct || 0))
    .slice(0, 10);

  const hasMonthly = mensual.some((item) => Number(item.consultas || 0) > 0 || Number(item.ventas || 0) > 0);
  const hasModelos = topModelos.length > 0;
  const hasInteres = interesModelos.length > 0;
  const hasConversion = conversionModelos.length > 0;
  const hasTipos = tiposVehiculo.some((item) => Number(item.total || 0) > 0);

  const kpiDescriptions = {
    consultas_vehiculos: "Consultas iniciadas en el período",
    modelos_consultados: "Modelos distintos consultados",
    vehiculos_vendidos: "Ventas registradas en el período",
    modelos_vendidos: "Modelos distintos vendidos",
    conversion_pct: "Vehículos vendidos / consultas",
    modelo_mas_consultado: "Modelo con más consultas",
    modelo_mas_vendido: "Modelo con más ventas",
    precio_promedio_vendido: "Monto vendido / vehículos vendidos",
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {[
          "consultas_vehiculos",
          "modelos_consultados",
          "vehiculos_vendidos",
          "modelos_vendidos",
          "conversion_pct",
          "modelo_mas_consultado",
          "modelo_mas_vendido",
          "precio_promedio_vendido",
        ].map((id) => (
          <KpiCard
            key={id}
            id={id}
            value={id.includes("modelo_mas") ? (kpis[id] || "Sin datos") : kpis[id]}
            currency={currency}
            description={kpiDescriptions[id]}
          />
        ))}
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <ChartPanel title="Consultas y ventas por mes" empty={!hasMonthly}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={mensual} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
              <Bar dataKey="consultas" name="Consultas" fill={colors.blue} radius={[5, 5, 0, 0]} />
              <Bar dataKey="ventas" name="Ventas" fill={colors.green} radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <ChartPanel title="Modelos consultados vs vendidos" empty={!hasModelos}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topModelos} layout="vertical" margin={{ top: 6, right: 18, left: 38, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="modelo" width={136} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
              <Bar dataKey="consultas" name="Consultas" fill={colors.blue} radius={[0, 5, 5, 0]} />
              <Bar dataKey="ventas" name="Ventas" fill={colors.green} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <ChartPanel title="Modelos con más interés pendiente" empty={!hasInteres}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={interesModelos} layout="vertical" margin={{ top: 6, right: 18, left: 38, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="modelo" width={136} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Bar dataKey="interes_pendiente" name="Activos + futuros" fill={colors.amber} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title="Conversión por modelo" empty={!hasConversion}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={conversionModelos} layout="vertical" margin={{ top: 6, right: 18, left: 38, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} tickFormatter={(value) => `${value}%`} />
              <YAxis type="category" dataKey="modelo" width={136} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => percent(value)} />
              <Bar dataKey="conversion_pct" name="Conversión" fill={colors.green} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box
        sx={{
          mt: 1.5,
        }}
      >
        <ChartPanel title="Ventas por tipo de vehículo" empty={!hasTipos}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={tiposVehiculo}
                dataKey="total"
                nameKey="label"
                innerRadius="48%"
                outerRadius="76%"
                paddingAngle={2}
                label={({ label, total }) => `${label}: ${total}`}
              >
                {tiposVehiculo.map((entry, index) => (
                  <Cell key={entry.label} fill={pieColors[index % pieColors.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            border: `1px solid ${colors.line}`,
            borderRadius: "8px",
            backgroundColor: colors.panel,
            boxShadow: "0 10px 24px rgba(15, 42, 89, 0.07)",
            p: { xs: 1.5, sm: 2 },
            minWidth: 0,
          }}
        >
          <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15, mb: 1.5 }}>
            Ranking de modelos consultados
          </Typography>
          <TableContainer sx={{ maxHeight: 380 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Modelo", "Consultas", "Ventas", "Activas", "Futuros", "Bajas", "Interés pendiente", "Conversión", "Monto", "Promedio"].map((head) => (
                    <TableCell
                      key={head}
                      align={head === "Modelo" ? "left" : "right"}
                      sx={{
                        color: colors.ink,
                        fontWeight: 950,
                        fontSize: 12,
                        bgcolor: "#f7f7f9",
                        borderColor: colors.line,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {modelos.map((row) => (
                  <TableRow key={row.modelo}>
                    <TableCell sx={{ ...tableCellSx, fontWeight: 850 }}>{row.modelo}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.consultas)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.ventas)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.activos)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.futuros)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.bajas)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.interes_pendiente)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{percent(row.conversion_pct)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatMoney(row.monto_vendido, currency)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatMoney(row.precio_promedio, currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
}
